import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/data/site";

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const t = TESTIMONIALS[idx];
  const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);
  const prev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-[#F8FAFC] py-24 md:py-32">
      <div className="pz-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <span className="pz-overline">Client voices</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
              Trusted by brands<br />across the UAE.
            </h2>
            <div className="pz-gold-line mt-6" />
            <p className="mt-6 text-slate-600">Real words from real partners who&apos;ve worked with us across launches, fit-outs, exhibitions and gifting programs.</p>

            <div className="mt-8 flex gap-3">
              <button
                onClick={prev}
                data-testid="testimonial-prev"
                className="w-12 h-12 rounded-full bg-white border border-slate-200 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] transition-all flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                data-testid="testimonial-next"
                className="w-12 h-12 rounded-full bg-[#0F172A] text-white hover:bg-[#2563EB] transition-all flex items-center justify-center"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-8 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.4 }}
                className="relative bg-white rounded-3xl p-10 sm:p-14 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.2)] border border-slate-100"
                data-testid={`testimonial-card-${idx}`}
              >
                <Quote className="absolute top-8 right-8 w-20 h-20 text-[#2563EB]/8" strokeWidth={1} />
                <div className="flex gap-1 text-[#F59E0B]">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="mt-7 font-display text-xl sm:text-2xl lg:text-3xl leading-snug text-[#0F172A] font-medium">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-9 flex items-center gap-4">
                  <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-md" />
                  <div>
                    <div className="font-display font-semibold text-[#0F172A]">{t.name}</div>
                    <div className="text-sm text-slate-500">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex gap-2 justify-center lg:justify-start">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-10 bg-[#0F172A]" : "w-4 bg-slate-300 hover:bg-slate-400"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
